import React, { useEffect, useMemo } from "react";
import AddIcon from "@mui/icons-material/Add";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import FieldHelpTooltip from "../../../../components/common/FieldHelpTooltip";
import { useStaticData } from "../../../../context/StaticDataContext";
import {
  ContactMethod,
  ContactType,
  ContactPreference,
} from "../../../types/types";

interface ContactCardProps {
  contacts: ContactMethod[];
  partyId?: number;
  onChange: (contacts: ContactMethod[]) => void;

  contactPreferences: ContactPreference[];
  onPreferenceChange: (prefs: ContactPreference[]) => void;
  allowAddAnother?: boolean;
  allowDelete?: boolean;
  allowDeleteDefaultContacts?: boolean;
  allowTypeChange?: boolean;
  ensureDefaultContacts?: boolean;
  emptyMessage?: string;
}

const generateTempId = () => Date.now() + Math.random();

const getContactTypeCode = (
  contact: ContactMethod,
  contactTypes: ContactType[] = [],
) =>
  (
    contact.contactTypeCode ||
    contactTypes.find((type) => type.contactTypeId === contact.contactTypeId)
      ?.code ||
    ""
  )
    .trim()
    .toUpperCase();

const isEmailType = (
  contact: ContactMethod,
  contactTypes: ContactType[] = [],
) => getContactTypeCode(contact, contactTypes) === "EMAIL";

const isMobileType = (
  contact: ContactMethod,
  contactTypes: ContactType[] = [],
) => getContactTypeCode(contact, contactTypes) === "MOBILE";

const getContactTypeName = (
  contact: ContactMethod,
  contactTypes: ContactType[] = [],
) =>
  contactTypes.find((type) => type.contactTypeId === contact.contactTypeId)
    ?.name ||
  contact.contactTypeCode ||
  "Contact";

export const isValidEmailAddress = (value: string) => {
  const trimmed = value.trim();

  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed);
};

export const isValidMobileNumber = (value: string) => {
  const normalised = value.replace(/[\s().-]/g, "");
  const isUkMobile = /^(?:\+?44|0)7\d{9}$/.test(normalised);
  const isInternationalMobile = /^\+[1-9]\d{9,14}$/.test(normalised);

  return isUkMobile || isInternationalMobile;
};

export const getContactValidationError = (
  contact: ContactMethod,
  contactTypes: ContactType[] = [],
) => {
  const value = contact.contactValue?.trim() ?? "";

  if (!value) return null;

  if (isEmailType(contact, contactTypes) && !isValidEmailAddress(value)) {
    return "Enter a valid email address.";
  }

  if (isMobileType(contact, contactTypes) && !isValidMobileNumber(value)) {
    return "Enter a valid mobile number.";
  }

  return null;
};

export const getContactValidationErrors = (
  contactMethods: ContactMethod[] = [],
  contactTypes: ContactType[] = [],
) =>
  contactMethods
    .map((contact) => ({
      contact,
      error: getContactValidationError(contact, contactTypes),
    }))
    .filter((result) => result.error);

export const getPersistedContactMethods = (
  contactMethods: ContactMethod[] = [],
) =>
  contactMethods
    .map(({ tempId, ...contact }) => ({
      ...contact,
      contactValue: contact.contactValue?.trim() ?? "",
    }))
    .filter((contact) => contact.contactValue.length > 0)
    .filter((contact) => !getContactValidationError(contact));

const ContactCard: React.FC<ContactCardProps> = ({
  contacts,
  partyId = 0,
  onChange,
  contactPreferences,
  onPreferenceChange,
  allowAddAnother = true,
  allowDelete = true,
  allowDeleteDefaultContacts = false,
  allowTypeChange = false,
  ensureDefaultContacts = true,
  emptyMessage = "Contact types are loading.",
}) => {
  const { data } = useStaticData();
  const contactTypes: ContactType[] = useMemo(
    () => data?.contactTypes ?? [],
    [data?.contactTypes],
  );

  /* =========================
   * Ensure tempId
   * ========================= */
  useEffect(() => {
    if (contacts.some((c) => !c.tempId)) {
      onChange(
        contacts.map((c) => ({
          ...c,
          tempId: c.tempId ?? generateTempId(),
        })),
      );
    }
  }, [contacts, onChange]);

  /* =========================
   * Default contacts
   * ========================= */
  useEffect(() => {
    if (!ensureDefaultContacts) return;
    if (!contactTypes.length) return;

    const defaultTypes = contactTypes.filter((contactType) =>
      ["EMAIL", "MOBILE"].includes(contactType.code),
    );

    const missingDefaults = defaultTypes
      .filter(
        (defaultType) =>
          !contacts.some(
            (contact) =>
              contact.contactTypeId === defaultType.contactTypeId ||
              contact.contactTypeCode === defaultType.code,
          ),
      )
      .map(
        (defaultType): ContactMethod => ({
          contactMethodId: 0,
          tempId: generateTempId(),
          partyId,
          contactTypeId: defaultType.contactTypeId,
          contactTypeCode: defaultType.code,
          contactValue: "",
          isVerified: true,
        }),
      );

    if (missingDefaults.length) {
      onChange([...contacts, ...missingDefaults]);
    }
  }, [contacts, contactTypes, partyId, onChange, ensureDefaultContacts]);

  /* =========================
   * Active types
   * ========================= */
  const activeContactTypeIds = useMemo(
    () => [
      ...new Set(
        contacts
          .filter((c) => c.contactTypeId && c.contactValue?.trim())
          .filter((c) => !getContactValidationError(c, contactTypes))
          .map((c) => c.contactTypeId),
      ),
    ],
    [contacts, contactTypes],
  );

  const sortedContacts = useMemo(
    () =>
      contacts
        .map((contact, index) => ({
          contact,
          index,
          sortOrder:
            contactTypes.find(
              (type) => type.contactTypeId === contact.contactTypeId,
            )?.sortOrder ?? Number.MAX_SAFE_INTEGER,
        }))
        .sort((left, right) => {
          if (left.sortOrder !== right.sortOrder) {
            return left.sortOrder - right.sortOrder;
          }

          return left.index - right.index;
        })
        .map(({ contact }) => contact),
    [contacts, contactTypes],
  );

  /* =========================
   * Sync preferences
   * ========================= */
  useEffect(() => {
    let updated = contactPreferences.filter((p) =>
      activeContactTypeIds.includes(p.contactTypeId),
    );

    activeContactTypeIds.forEach((typeId) => {
      if (!updated.some((p) => p.contactTypeId === typeId)) {
        const ct = contactTypes.find((c) => c.contactTypeId === typeId);

        updated.push({
          preferenceId: 0,
          partyId,
          contactTypeId: typeId,
          contactTypeCode: ct?.code ?? "",
          allowed: true,
          consentAtUtc: new Date().toISOString(),
          expiresAtUtc: null,
          notes: "",
        });
      }
    });

    if (JSON.stringify(updated) !== JSON.stringify(contactPreferences)) {
      onPreferenceChange(updated);
    }
  }, [
    activeContactTypeIds,
    contactPreferences,
    contactTypes,
    onPreferenceChange,
    partyId,
  ]);

  /* =========================
   * Handlers
   * ========================= */

  const updateContact = (id: number, changes: Partial<ContactMethod>) => {
    onChange(contacts.map((c) => (c.tempId === id ? { ...c, ...changes } : c)));
  };

  const deleteContact = (id: number) => {
    onChange(contacts.filter((c) => c.tempId !== id));
  };

  const addContact = () => {
    const newContact: ContactMethod = {
      contactMethodId: 0,
      tempId: generateTempId(),
      partyId,
      contactTypeId: 0,
      contactTypeCode: "",
      contactValue: "",
      isVerified: true,
    };

    onChange([...contacts, newContact]);
  };

  const togglePreference = (typeId: number, checked: boolean) => {
    onPreferenceChange(
      contactPreferences.map((p) =>
        p.contactTypeId === typeId ? { ...p, allowed: checked } : p,
      ),
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getIcon = (code?: string) => {
    switch (code) {
      case "EMAIL":
        return "📧";
      case "MOBILE":
        return "📱";
      case "WHATSAPP":
        return "💬";
      default:
        return "🔹";
    }
  };

  const getContactTypeIcon = (code?: string) => {
    switch (code) {
      case "EMAIL":
        return <EmailOutlinedIcon fontSize="small" />;
      case "MOBILE":
        return <SmartphoneIcon fontSize="small" />;
      case "WHATSAPP":
        return <ChatBubbleOutlineIcon fontSize="small" />;
      default:
        return <ContactPhoneOutlinedIcon fontSize="small" />;
    }
  };

  /* =========================
   * Render
   * ========================= */

  return (
    <div className="card">
      <div className="card-header party-card-header">
        <div className="card-header-row party-card-header-row">
          <div className="party-card-title-group">
            <div className="card-title">
              <span>Contacts</span>
              <FieldHelpTooltip text="Add every contact method the party uses." />
            </div>
          </div>

        {allowAddAnother && (
          <button
            type="button"
            className="link-button link-button-with-icon card-header-link-action"
            onClick={addContact}
          >
            <AddIcon fontSize="small" />
            Add Another
          </button>
        )}
        </div>
      </div>

      <div className="contact-card-content">
        {activeContactTypeIds.length > 0 && (
          <div className="contact-permissions-panel">
            <div className="contact-permissions-header">
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <h3 className="contact-permissions-title">
                  Contact Permissions
                </h3>
                <FieldHelpTooltip text="Confirm which contact methods this party has given permission for us to use." />
              </div>
            </div>

            <div className="contact-permissions-list">
              {activeContactTypeIds.map((typeId) => {
                const ct = contactTypes.find((x) => x.contactTypeId === typeId);
                const pref = contactPreferences.find(
                  (p) => p.contactTypeId === typeId,
                );
                const isAllowed = pref?.allowed ?? true;

                return (
                  <label
                    key={typeId}
                    className={`contact-permission-item ${
                      isAllowed ? "is-allowed" : "is-blocked"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="contact-permission-checkbox"
                      checked={isAllowed}
                      onChange={(e) =>
                        togglePreference(typeId, e.target.checked)
                      }
                    />
                    <span className="contact-permission-copy">
                      <span className="contact-permission-name">
                        {ct?.name}
                      </span>
                      <span className="contact-permission-state">
                        {isAllowed
                          ? "Permission granted"
                          : "Do not contact using this method"}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        <div className="contact-card-main">
          {!contacts.length && (
            <p className="muted">{emptyMessage}</p>
          )}

          {sortedContacts.map((contact, index) => {
            const contactTypeCode = getContactTypeCode(contact, contactTypes);
            const contactTypeName = getContactTypeName(contact, contactTypes);
            const hasSelectedContactType = Boolean(contact.contactTypeId);
            const showContactTypeSelect =
              allowTypeChange || !hasSelectedContactType;
            const isDefaultContact = ["EMAIL", "MOBILE"].includes(contactTypeCode);
            const validationError = getContactValidationError(
              contact,
              contactTypes,
            );

            return (
              <div
                key={
                  contact.tempId ??
                  (contact.contactMethodId > 0
                    ? `contact-method-${contact.contactMethodId}`
                    : `draft-contact-${index}`)
                }
                className="contact-edit-container"
              >
                <div
                  className={`contact-edit-row ${
                    !showContactTypeSelect ? "contact-edit-row--icon" : ""
                  }`}
                >
                  {!showContactTypeSelect ? (
                    <div
                      className="contact-type-icon-badge"
                      title={contactTypeName}
                      aria-label={contactTypeName}
                    >
                      {getContactTypeIcon(contactTypeCode)}
                    </div>
                  ) : (
                    <div className="form-group">
                      <label className="form-label">Contact Type</label>
                      <select
                        className="form-input"
                        value={contact.contactTypeId || ""}
                        onChange={(e) => {
                          const id = Number(e.target.value);
                          const ct = contactTypes.find(
                            (x) => x.contactTypeId === id,
                          );

                          updateContact(contact.tempId!, {
                            contactTypeId: id,
                            contactTypeCode: ct?.code ?? "",
                          });
                        }}
                      >
                        <option value="">Select Contact Type</option>
                        {contactTypes.map((ct) => (
                          <option
                            key={ct.contactTypeId}
                            value={ct.contactTypeId}
                          >
                            {ct.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">{contactTypeName}</label>
                    <input
                      className="form-input"
                      value={contact.contactValue}
                      onChange={(e) =>
                        updateContact(contact.tempId!, {
                          contactValue: e.target.value,
                        })
                      }
                      placeholder={`Enter ${contactTypeName.toLowerCase()}`}
                      aria-invalid={Boolean(validationError)}
                    />
                    {validationError && (
                      <div className="form-error">{validationError}</div>
                    )}
                  </div>

                  {allowDelete &&
                    (allowDeleteDefaultContacts || !isDefaultContact) && (
                      <button
                        type="button"
                        className="icon-button contact-delete-button"
                        onClick={() => deleteContact(contact.tempId!)}
                        aria-label="Delete contact"
                      >
                        <DeleteIcon fontSize="small" />
                      </button>
                    )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ContactCard;
